
<template>
  <div :class="`${getClassName('wrapper')} autocomplete-wrapper`">
    <input
      ref="input"
      type="text"
      :id="id"
      :class="`${getClassName('input')} autocomplete-input`"
      :placeholder="placeholder"
      :name="name"
      v-model="type"
      @input="handleInput"
      @dblclick="handleDoubleClick"
      @blur="handleBlur"
      @keydown="handleKeyDown"
      @focus="handleFocus"
      autocomplete="off"
    />

    <div
      :class="`${getClassName('list')} autocomplete autocomplete-list`"
      v-show="showList && json && json.length"
    >
      <ul>
        <li
          v-for="(data, i) in json"
          :class="activeClass(i)"
        >
          <a
            href="#"
            @click.prevent="selectList(data)"
            @mousemove="mousemove(i)"
          >
            <div v-if="onShouldRenderChild" v-html="onShouldRenderChild(data)"></div>
            <div v-if="!onShouldRenderChild">
              <b class="autocomplete-anchor-text">{{ deepValue(data, anchor) }}</b>
              <span class="autocomplete-anchor-label">{{ deepValue(data, label) | stripCountry }}</span>
            </div>
          </a>
        </li>
      </ul>

    </div>

  </div>
</template>


<script>
  /*! Based on:
  * Vue 2 Autocomplete @ Version 0.0.1
  * Copyright (c) 2016 Naufal Rabbani (http://github.com/BosNaufal)
  * Licensed Under MIT (http://opensource.org/licenses/MIT)
  *
  */
  import axios from 'axios';

  export default {
    filters: {
      stripCountry(value) {
        if (!value) return ''
        value = value.toString()
        return value.replace(', United States', '')
      }
    },
    props: {
      id: String,
      name: String,
      className: String,
      classes: {
        type: Object,
        default: () => ({
          wrapper: false,
          input: false,
          list: false,
          item: false,
        })
      },
      placeholder: String,

      // Intial Value
      initValue: {
        type: String,
        default: ""
      },

      // Manual List
      options: Array,

      // Filter After Get the data
      filterByAnchor: {
        type: Boolean,
        default: true,
      },

      // Anchor of list
      anchor: {
        type: String,
        required: true
      },

      // Label of list
      label: String,

      // Debounce time
      debounce: Number,

      // ajax URL will be fetched
      url: {
        type: String,
        required: true
      },

      urlQuery: {
        type: Boolean,
        default: true,
      },

      // Custom Params
      customParams: Object,

      // minimum length
      min: {
        type: Number,
        default: 1
      },

      // Create a custom template from data.
      onShouldRenderChild: Function,

      // Callback
      onInput: Function,
      onShow: Function,
      onBlur: Function,
      onHide: Function,
      onFocus: Function,
      onSelect: Function,
      onError: Function,
    },

    data() {
      return {
        showList: false,
        type: "",
        json: [],
        focusList: 0,
        debounceTask: undefined,
      };
    },

    watch: {
      options(newVal, oldVal) {
        if (this.filterByAnchor) {
          const { type, anchor } = this
          const regex = new RegExp(`${type}`, 'i')
          const filtered = newVal.filter((item) => {
            const found = item[anchor].search(regex) !== -1
            return found
          })
          this.json = filtered
        } else {
          this.json = newVal
        }
      }
    },

    methods: {

      getClassName(part) {
        const { classes, className } = this
        if (classes[part]) return `${classes[part]}`
        return className ? `${className}-${part}` : ''
      },

      // Neutralize Autocomplete
      clearInput() {
        this.showList = false
        this.type = ""
        this.json = []
      },

      // Get the original data
      cleanUp(data){
        return JSON.parse(JSON.stringify(data));
      },


      /*==============================
        INPUT EVENTS
      =============================*/
      handleInput(e){
        const { value } = e.target
        this.showList = true;
        // Callback Event
        if(this.onInput) this.onInput(value)
        // If Debounce
        if (this.debounce) {
          if (this.debounceTask !== undefined) clearTimeout(this.debounceTask)
          this.debounceTask = setTimeout(() => {
            return this.getData(value)
          }, this.debounce)
        } else {
          return this.getData(value)
        }
      },

      handleKeyDown(e){

        let key = e.keyCode;

        // Disable when list isn't showing up
        if(!this.showList) return;

        // Key List
        const DOWN = 40
        const UP = 38
        const ENTER = 13
        const ESC = 27

        // Prevent Default for Prevent Cursor Move & Form Submit
        switch (key) {
          case DOWN:
            e.preventDefault()
            this.focusList++;
          break;
          case UP:
            e.preventDefault()
            this.focusList--;
          break;
          case ENTER:
            e.preventDefault()
            if(!this.json || this.json.length < 1) {
              this.clearInput();
              return;
            }
            this.selectList(this.json[this.focusList])
            this.showList = false;
            // Blur input
            this.$refs.input.blur();
          break;
          case ESC:
            this.clearInput();
          break;
        }
        const listLength = this.json.length - 1;
        const outOfRangeBottom = this.focusList > listLength
        const outOfRangeTop = this.focusList < 0
        const topItemIndex = 0
        const bottomItemIndex = listLength

        let nextFocusList = this.focusList
        if (outOfRangeBottom) nextFocusList = topItemIndex
        if (outOfRangeTop) nextFocusList = bottomItemIndex
        this.focusList = nextFocusList
      },

      setValue(val) {
        this.type = val
      },


      /*==============================
        LIST EVENTS
      =============================*/

      handleDoubleClick(){
        this.json = [];
        this.getData("")
        // Callback Event
        this.onShow ? this.onShow() : null
        this.showList = true;
      },

      handleBlur(e){
        // Callback Event
        this.onBlur ? this.onBlur(e) : null
        setTimeout(() => {
          // Callback Event
          this.onHide ? this.onHide() : null
          this.showList = false;
        }, 250);
      },

      handleFocus(e){
        this.focusList = 0;
        // Callback Event
        this.onFocus ? this.onFocus(e) : null;
        // Select text
        e.currentTarget.select();
      },

      mousemove(i){
        this.focusList = i;
      },

      activeClass(i){
        const focusClass = i === this.focusList ? 'focus-list' : ''
        return `${focusClass}`
      },

      selectList(data){
        if (!data) return;
        // Deep clone of the original object
        const clean = this.cleanUp(data);
        // Put the selected data to type (model)
        this.type = clean[this.anchor];
        // Hide List
        this.showList = false;
        // Callback Event
        this.onSelect ? this.onSelect(clean) : null;
      },

      deepValue(obj, path) {
        const arrayPath = path.split('.')
        for (var i = 0; i < arrayPath.length; i++) {
          obj = obj[arrayPath[i]];
        }
        return obj;
      },


      /*==============================
        AJAX EVENTS
      =============================*/

      composeQueryUrl(val) {
        let url = this.url + encodeURIComponent(val) + '.json';
        return url
      },

      getData (val) {
        if (val.length < this.min || !this.url) {
          this.clearInput();
          return;
        }

        // Compose URL
        let url =  this.urlQuery ? this.composeQueryUrl(val) : this.url;

        const axiosConfig = {
          timeout: 15000,
          params: this.customParams
        }
        axios.get(url, axiosConfig)
         .then(response => {
          // One last check to be sure we have displayable results
          if (response.data && response.data.features) {
            this.json = response.data.features;
          }
          else {
            this.clearInput();
          }
        })
        .catch(error => {
          // Error callback
          if(this.onError) this.onError(error)
          this.clearInput();
        });
      },
    },

    created(){
      // Sync parent model with initValue Props
      this.type = this.initValue ? this.initValue : null
    }

  }
</script>

<style lang="scss">
@import '~styles/variables.scss';

// .autocomplete-wrapper {
//   z-index: 3001;
// }

.transition, .autocomplete, .showAll-transition, .autocomplete ul, .autocomplete ul li a{
  transition:all 0.3s ease-out;
  -moz-transition:all 0.3s ease-out;
  -webkit-transition:all 0.3s ease-out;
  -o-transition:all 0.3s ease-out;
}
// search input
.autocomplete-input {
  font-size: 18px;
  padding: 10px 10px 10px 45px;
  width: 100%;
  height: 45px;
  border: 1px solid $lightGray;
  border-radius: 3px;
  box-sizing: border-box;
  position: relative;
  z-index: 1000;

  background-image: url('../assets/icons/icon-search-aaaaaa.svg');
  background-repeat: no-repeat;
  background-position: left;
  background-position-x: 15px;
}
// autocomplete results
.autocomplete-list {
  position: absolute;
  bottom: -15px;
  left: -15px;
  width: calc(100% + 30px);
  padding-bottom: 45px;
  text-align: left;
  background: white;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.3);
}
.showAutocompleteBelow .autocomplete-list {
  bottom: unset;
  top: -15px;
  padding-bottom: unset;
  padding-top: 55px;
}
.autocomplete ul{
  list-style: none;
  margin: 0;
  padding: 15px 15px 20px;
  width: 100%;
  box-sizing: border-box;
}
.autocomplete ul li a{
  text-decoration: none;
  display: block;
  background: white;
  color: $grey-60;
  padding: 10px;
}

// Selected element
.autocomplete ul li a:hover, .autocomplete ul li.focus-list a{
  background: $hoverBackgroundColor;
  color: $darkTextColor;
}

.autocomplete ul li a .autocomplete-anchor-label{
  display: block;
  margin-top: 5px;
  color: $grey-40;
  font-size: 0.8125rem;
  line-height: 1.5;
}

.mq-mobile {
  .showAutocompleteBelow .autocomplete-list,
  .autocomplete-list {
    bottom: unset;
    top: -1px;
    left: 0;
    width: 100%;
    padding-top: 0;
    z-index: 4000;
    position: relative;
    height: 30vh;
    overflow: scroll;
    box-sizing: border-box;
    border: 1px solid $lightGray;
    border-top: none;
    box-shadow: none;
  }
  .expanded .autocomplete-list {
    margin-bottom: calc(-#{$tinelineContainerHeight} - #{$annotationHeight} - 0.5em);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Autocomplete.vue